import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Mail, User, Bell, BellOff, Paperclip, X, Download, FileText, MessageSquare } from "lucide-react";

function formatTime(iso) {
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}

/**
 * Phase 154 — Recruiter messages queue.
 *
 * A sidebar + conversation layout for recruiters/employers to browse every
 * candidate thread in one place with an Active vs Snoozed toggle so paused
 * threads don't clutter the live queue. Deep-link friendly: `/messages/:threadId`
 * opens the thread directly.
 */
export default function RecruiterMessages() {
    const { threadId: deepLinkThreadId } = useParams();
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState("active"); // "active" | "snoozed"
    const [threads, setThreads] = useState([]);
    const [activeId, setActiveId] = useState(deepLinkThreadId || null);
    const [thread, setThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [loadingList, setLoadingList] = useState(true);
    const [loadingThread, setLoadingThread] = useState(false);
    const [snoozeOpen, setSnoozeOpen] = useState(false);
    const [pending, setPending] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const bottomRef = useRef(null);
    const pollRef = useRef(null);
    const [counts, setCounts] = useState({ active: null, snoozed: null });

    const loadThreads = useCallback(async () => {
        setLoadingList(true);
        try {
            const { data } = await api.get("/messages/threads", { params: { status: statusFilter } });
            setThreads(data || []);
            if (!activeId && (data || []).length) setActiveId(deepLinkThreadId || data[0].id);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoadingList(false); }
    }, [statusFilter, activeId, deepLinkThreadId]);

    // Count badges (fire-and-forget — small cost, keeps the toggle informative)
    const loadCounts = useCallback(async () => {
        try {
            const [a, s] = await Promise.all([
                api.get("/messages/threads", { params: { status: "active" } }),
                api.get("/messages/threads", { params: { status: "snoozed" } }),
            ]);
            setCounts({ active: (a.data || []).length, snoozed: (s.data || []).length });
        } catch { /* non-fatal */ }
    }, []);

    const loadThread = useCallback(async (id) => {
        if (!id) { setThread(null); setMessages([]); return; }
        setLoadingThread(true);
        try {
            const { data } = await api.get(`/messages/threads/${id}`);
            setThread(data);
            setMessages(data.messages || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoadingThread(false); }
    }, []);

    useEffect(() => { loadThreads(); }, [loadThreads]);
    useEffect(() => { loadCounts(); }, [loadCounts, threads.length]);
    useEffect(() => { loadThread(activeId); }, [activeId, loadThread]);

    useEffect(() => {
        if (!activeId) return;
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/messages/threads/${activeId}`);
                setMessages(data.messages || []);
            } catch { /* ignore */ }
        }, 10000);
        return () => clearInterval(pollRef.current);
    }, [activeId]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages.length]);

    const pickFiles = async (files) => {
        if (!files?.length) return;
        setUploading(true);
        try {
            const out = [];
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                if (activeId) fd.append("thread_id", activeId);
                const { data } = await api.post("/messages/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
                out.push(data);
            }
            setPending((p) => [...p, ...out]);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const removePending = (id) => setPending((p) => p.filter((x) => x.id !== id));

    const downloadAttachment = async (att) => {
        try {
            const r = await api.get(`/messages/attachments/${att.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = att.filename;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const send = async (e) => {
        e?.preventDefault?.();
        const body = draft.trim();
        if ((!body && pending.length === 0) || !activeId) return;
        setSending(true);
        try {
            const attachment_ids = pending.map((p) => p.id);
            const { data: msg } = await api.post(`/messages/threads/${activeId}/reply`, { body: body || "(sent an attachment)", attachment_ids });
            setMessages((m) => [...m, msg]);
            setDraft(""); setPending([]);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSending(false); }
    };

    const snooze = async (preset) => {
        if (!activeId) return;
        try {
            if (preset === "clear") {
                await api.post(`/messages/threads/${activeId}/snooze`, { until: null });
                setThread((t) => ({ ...(t || {}), snoozed_until: null }));
                toast.success("Thread un-snoozed");
            } else {
                const { data } = await api.post(`/messages/threads/${activeId}/snooze-preset`, {
                    preset,
                    tz_offset_min: new Date().getTimezoneOffset(),
                });
                setThread((t) => ({ ...(t || {}), snoozed_until: data.snoozed_until }));
                toast.success(`Snoozed until ${new Date(data.snoozed_until).toLocaleString()}`);
            }
            setSnoozeOpen(false);
            loadThreads();
            loadCounts();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const isSnoozed = thread?.snoozed_until && new Date(thread.snoozed_until) > new Date();
    const activeThread = threads.find((t) => t.id === activeId);

    return (
        <div className="max-w-7xl mx-auto p-3 sm:p-6 lg:p-8" data-testid="recruiter-messages-page">
            <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Inbox</div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-1">Candidate messages</h1>
                    <p className="text-muted-foreground text-sm mt-2 max-w-xl">
                        Threads with candidates across every job. Snooze a conversation to clear it from your queue until you want it back.
                    </p>
                </div>
                {/* Snoozed vs Active toggle */}
                <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="thread-status-toggle">
                    <button
                        type="button"
                        onClick={() => { setStatusFilter("active"); setActiveId(null); }}
                        className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest transition ${statusFilter === "active" ? "bg-ikb text-white" : "bg-white hover:bg-secondary"}`}
                        data-testid="filter-active"
                    >
                        Active {counts.active != null && <span className="opacity-70 ml-1">({counts.active})</span>}
                    </button>
                    <button
                        type="button"
                        onClick={() => { setStatusFilter("snoozed"); setActiveId(null); }}
                        className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest transition border-l border-border ${statusFilter === "snoozed" ? "bg-amber-500 text-white" : "bg-white hover:bg-secondary"}`}
                        data-testid="filter-snoozed"
                    >
                        Snoozed {counts.snoozed != null && <span className="opacity-70 ml-1">({counts.snoozed})</span>}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-border border border-border min-h-[60vh]">
                <aside className="md:col-span-4 bg-white overflow-y-auto max-h-[75vh]" data-testid="thread-sidebar">
                    {loadingList ? (
                        <div className="p-6 text-xs text-muted-foreground font-mono">Loading...</div>
                    ) : threads.length === 0 ? (
                        <div className="p-6 text-sm text-muted-foreground text-center">
                            <MessageSquare className="w-6 h-6 mx-auto mb-2 text-muted-foreground/50" />
                            {statusFilter === "snoozed" ? "No snoozed threads right now." : "No active conversations yet. Message a candidate from their profile to start one."}
                        </div>
                    ) : (
                        threads.map((t) => {
                            const active = t.id === activeId;
                            const snoozedActive = t.snoozed_until && new Date(t.snoozed_until) > new Date();
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => { setActiveId(t.id); navigate(`/messages/${t.id}`); }}
                                    data-testid={`recruiter-thread-${t.id}`}
                                    className={`w-full text-left p-4 border-b border-border transition-colors ${active ? "bg-blue-50/60 border-l-2 border-l-ikb" : "hover:bg-secondary border-l-2 border-l-transparent"}`}
                                >
                                    <div className="font-medium text-sm flex items-center gap-2">
                                        <User className="w-3.5 h-3.5 text-ikb shrink-0" />
                                        <span className="truncate">{t.candidate_name || t.candidate_email || "Candidate"}</span>
                                        {snoozedActive && (
                                            <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono text-amber-700" data-testid="row-snooze-badge">
                                                <BellOff className="w-3 h-3" />
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1 truncate">{t.subject || "Conversation"}</div>
                                    <div className="text-[10px] text-muted-foreground font-mono mt-1">{formatTime(t.last_message_at || t.created_at)}</div>
                                </button>
                            );
                        })
                    )}
                </aside>

                <section className="md:col-span-8 bg-white flex flex-col max-h-[75vh]">
                    {!activeId ? (
                        <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                            Select a conversation to read messages.
                        </div>
                    ) : (
                        <>
                            <div className="p-4 border-b border-border flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="font-display font-bold text-base flex items-center gap-2">
                                        <User className="w-4 h-4 text-ikb" /> {activeThread?.candidate_name || activeThread?.candidate_email || "Candidate"}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                                        <Mail className="w-3 h-3" /> {activeThread?.candidate_email || "no email"}
                                        {activeThread?.subject && <span>· {activeThread.subject}</span>}
                                    </div>
                                    {isSnoozed && (
                                        <div className="mt-2 text-[11px] bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1 font-mono inline-flex items-center gap-1" data-testid="thread-snoozed-banner">
                                            <BellOff className="w-3 h-3" /> Snoozed until {new Date(thread.snoozed_until).toLocaleString()}
                                        </div>
                                    )}
                                </div>
                                <div className="relative shrink-0">
                                    <Button
                                        type="button"
                                        variant={isSnoozed ? "default" : "outline"}
                                        size="sm"
                                        className={`rounded-none h-8 text-[11px] font-mono uppercase tracking-widest ${isSnoozed ? "bg-amber-500 hover:bg-amber-600 border-amber-500 text-white" : ""}`}
                                        onClick={() => setSnoozeOpen((o) => !o)}
                                        data-testid="thread-snooze-btn"
                                    >
                                        {isSnoozed ? <BellOff className="w-3 h-3 mr-1" /> : <Bell className="w-3 h-3 mr-1" />}
                                        {isSnoozed ? "Snoozed" : "Snooze"}
                                    </Button>
                                    {snoozeOpen && (
                                        <div className="absolute right-0 top-9 bg-white border border-border rounded-md shadow-md py-1 z-10 w-48" data-testid="thread-snooze-menu">
                                            <button type="button" onClick={() => snooze("later_today")} className="w-full px-3 py-2 text-left text-xs hover:bg-secondary">Later today</button>
                                            <button type="button" onClick={() => snooze("tomorrow_morning")} className="w-full px-3 py-2 text-left text-xs hover:bg-secondary">Tomorrow 9am</button>
                                            <button type="button" onClick={() => snooze("next_tuesday")} className="w-full px-3 py-2 text-left text-xs hover:bg-secondary">Next Tuesday 9am</button>
                                            <button type="button" onClick={() => snooze("next_week")} className="w-full px-3 py-2 text-left text-xs hover:bg-secondary">Next week</button>
                                            {isSnoozed && (
                                                <>
                                                    <div className="border-t border-border my-1" />
                                                    <button type="button" onClick={() => snooze("clear")} className="w-full px-3 py-2 text-left text-xs hover:bg-secondary text-rose-700" data-testid="thread-unsnooze">Clear snooze</button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-5 bg-secondary/40 space-y-3" data-testid="recruiter-message-list">
                                {loadingThread ? (
                                    <div className="text-xs text-muted-foreground font-mono">Loading...</div>
                                ) : messages.map((m) => {
                                    const mine = m.sender_role !== "seeker" && m.sender_role !== "candidate";
                                    return (
                                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                            <div className={`max-w-[80%] border px-3 py-2 ${mine ? "bg-ikb text-white border-ikb" : "bg-white border-border"}`}>
                                                <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                                                    {m.sender_name || (mine ? "You" : "Candidate")} · {formatTime(m.created_at)}
                                                </div>
                                                <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                                                {(m.attachments || []).length > 0 && (
                                                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                                                        {m.attachments.map((a) => (
                                                            <button
                                                                key={a.id}
                                                                type="button"
                                                                onClick={() => downloadAttachment(a)}
                                                                className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 border rounded-md max-w-[220px] truncate ${mine ? "border-white/40 hover:bg-white/10" : "border-border hover:bg-secondary/80"}`}
                                                                title={`${a.filename} · ${Math.round((a.size || 0) / 1024)} KB`}
                                                            >
                                                                <FileText className="w-3 h-3 shrink-0" />
                                                                <span className="truncate">{a.filename}</span>
                                                                <Download className="w-3 h-3 shrink-0 opacity-70" />
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={bottomRef} />
                            </div>

                            <form onSubmit={send} className="border-t border-border p-4">
                                <Textarea
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e); }}
                                    placeholder="Write a reply...  (Cmd/Ctrl+Enter to send)"
                                    rows={2}
                                    className="rounded-none resize-none"
                                    data-testid="recruiter-message-input"
                                />
                                {pending.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {pending.map((p) => (
                                            <span key={p.id} className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 bg-secondary border border-border rounded-md">
                                                <FileText className="w-3 h-3" />
                                                <span className="max-w-[160px] truncate">{p.filename}</span>
                                                <button type="button" onClick={() => removePending(p.id)} className="hover:text-destructive" aria-label="Remove">
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="flex items-center justify-between mt-3">
                                    <input ref={fileRef} type="file" hidden multiple onChange={(e) => pickFiles(e.target.files)} />
                                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-none">
                                        <Paperclip className="w-4 h-4 mr-1.5" /> {uploading ? "Uploading…" : "Attach"}
                                    </Button>
                                    <Button type="submit" disabled={sending || (!draft.trim() && pending.length === 0)} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="recruiter-message-send">
                                        <Send className="w-4 h-4 mr-2" /> {sending ? "Sending..." : "Send"}
                                    </Button>
                                </div>
                            </form>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}
